import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useSWR from "swr";
import { SpineTemplate } from "../../../types/spine-template";
import Loader from "@/components/ui/Loader";
import MyPagination from "components/ui/MyPagination";
import { TemplateItem } from "./TemplateItem";

interface TemplateListProps {
  currentTid: string;
  selectedTemplateIds: string[];
  onSelectTemplate: (template: Partial<SpineTemplate>) => void;
  templateCache: Map<string, Partial<SpineTemplate>>;
}

export function TemplateList({
  currentTid,
  selectedTemplateIds,
  onSelectTemplate,
}: TemplateListProps) {
  const [searchTid, setSearchTid] = useState("");
  const [type, setType] = useState<string>("");
  const [after, setAfter] = useState<string | null>(null);
  const [afters, setAfters] = useState<string[]>([]);

  const resetPagination = () => {
    setAfter(null);
    setAfters([]);
  };

  const { data: templates, isLoading } = useSWR<SpineTemplate[]>([
    "/spine-templates",
    {
      status: undefined,
      includeChildren: true,
      ...(searchTid ? { tid: searchTid } : {}),
      ...(type ? { type } : {}),
      ...(after ? { after } : {}),
    },
  ]);

  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    return templates.filter((template) => {
      if (template.id === currentTid) return false;
      if (selectedTemplateIds.includes(template.id)) return false;
      return true;
    });
  }, [templates, currentTid, selectedTemplateIds]);

  const nextAfter = useMemo(() => {
    if (!templates || templates.length === 0) return null;
    return templates[templates.length - 1]?.id || null;
  }, [templates]);

  const goAfter = (next: string | null) => {
    setAfter(next);
    if (next === null) {
      setAfters([]);
    } else {
      const index = afters.indexOf(next);
      if (index === -1) setAfters([...afters, next]);
      else setAfters(afters.slice(0, index + 1));
    }
  };

  const handleSelect = (template: Partial<SpineTemplate>) => {
    if (!template.id) return;
    onSelectTemplate(template);
  };

  const handleClearType = () => {
    setType("");
    resetPagination();
  };

  return (
    <Card className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden shadow-none">
      <CardHeader className="shrink-0 p-3">
        <CardTitle className="text-sm">全部模板</CardTitle>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-3 pt-0">
        <div className="mb-3 flex shrink-0 flex-wrap items-center gap-3">
          {!isLoading && (
            <MyPagination
              nextAfter={nextAfter}
              after={after}
              goAfter={goAfter}
              afters={afters}
              className="m-0 w-auto"
            />
          )}
          <div className="relative w-[200px]">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Spine Tid"
              value={searchTid}
              onChange={(e) => {
                setSearchTid(e.target.value);
                resetPagination();
              }}
              className="pl-8 pr-8"
            />
            {searchTid && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTid("");
                  resetPagination();
                }}
                className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="relative">
            <Select
              value={type ?? undefined}
              onValueChange={(value) => {
                setType(value as string);
                setAfter(null);
                setAfters([]);
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select a Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Type</SelectLabel>
                  <SelectItem value="character">character</SelectItem>
                  <SelectItem value="bg">bg</SelectItem>
                  <SelectItem value="fg">fg</SelectItem>
                  <SelectItem value="bgEffect">bgEffect</SelectItem>
                  <SelectItem value="fgEffect">fgEffect</SelectItem>
                  <SelectItem value="bgStatic">bgStatic</SelectItem>
                  <SelectItem value="fgStatic">fgStatic</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            {type && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearType}
                className="absolute right-8 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <Separator className="mb-3 shrink-0" />

        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Loader>加载模板列表...</Loader>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {searchTid ? "未找到匹配的模板" : "暂无其他模板"}
            </div>
          ) : (
            <div className="space-y-2 pr-1">
              {filteredTemplates.map((template) => (
                <TemplateItem
                  key={template.id}
                  template={template}
                  onSelect={() => handleSelect(template)}
                  showAction={true}
                  isSelected={false}
                />
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
